import { FILTER_IDS, getAnnoFilterBucketKey } from "@okf/ootils";
import { getFilterIcon } from "@/components/inputs/DynamicTagsInputs/FilterInput/comps/DocHierarchyFilter/utils/getFilterIcon";

const ANNO_HIERARCHY_FILTER_IDS = new Set(Object.values(FILTER_IDS));

const LEVEL_META = {
  [FILTER_IDS.themes]: {
    level: "themes",
    levelIcon: "FolderSimple",
    levelLabel: "Themes",
  },
  [FILTER_IDS.subThemes]: {
    level: "subThemes",
    levelIcon: "FolderSimple",
    levelLabel: "Sub-Themes",
  },
  [FILTER_IDS.tags]: {
    level: "tags",
    levelIcon: "Tag",
    levelLabel: "Tags",
  },
};

/**
 * Every non-hierarchy annotation filter config bundled in the annoHierarchyType
 * config: the common annotation filters (Author, Document Field, …) plus, in
 * split mode, the Topics (clustering-tree) filter that rides in
 * `source.topicClusteringFilterConfig`. All of them render as one applied
 * block each, edited through their own filter input.
 */
const getBundledAnnoFilterConfigs = (annoFilterConfig) => {
  const commonFilters = annoFilterConfig?.source?.commonAnnotationFilters || [];
  const topicsFilter = annoFilterConfig?.source?.topicClusteringFilterConfig;
  return topicsFilter ? [...commonFilters, topicsFilter] : commonFilters;
};

const getCommonAnnoFilterIds = (annoFilterConfig) =>
  new Set(getBundledAnnoFilterConfigs(annoFilterConfig).map((f) => f.filterId));

/**
 * Checks if a filter belongs to the annotation hierarchy (themes/subThemes/tags)
 * or is a common annotation filter bundled in the annoHierarchyType config.
 */
export const isAnnoFilter = (filter, annoFilterConfig) => {
  if (ANNO_HIERARCHY_FILTER_IDS.has(filter.filterId)) return true;
  if (annoFilterConfig) {
    return getCommonAnnoFilterIds(annoFilterConfig).has(filter.filterId);
  }
  return false;
};

/**
 * Groups annotation hierarchy active filters into block descriptors.
 *
 * Groups first by filterId (level), then within each level by parent path.
 * Also creates blocks for common annotation filters (e.g. Author, Document Field).
 * When savedConfigs are provided, generates empty blocks (values: []) for saved
 * configs that have no active values, preserving group structure via _savedGroups.
 *
 * @param {Array} activeFilters - The active filters array
 * @param {Object} [annoFilterConfig] - The annoHierarchyType filter config
 * @param {Array} [savedConfigs] - Saved filter configs (from localStorage) for empty block generation
 * @returns {Array<BlockDescriptor>}
 */
export const groupAnnoFilters = (activeFilters, annoFilterConfig, savedConfigs = []) => {
  const commonAnnoFilterIds = getCommonAnnoFilterIds(annoFilterConfig);
  const commonAnnoFilterMap = new Map();
  for (const f of getBundledAnnoFilterConfigs(annoFilterConfig)) {
    commonAnnoFilterMap.set(f.filterId, f);
  }

  const blocks = [];

  for (const filter of activeFilters) {
    const isHierarchy = ANNO_HIERARCHY_FILTER_IDS.has(filter.filterId);
    const isCommonAnno = commonAnnoFilterIds.has(filter.filterId);

    if (!isHierarchy && !isCommonAnno) continue;

    // Common annotation filters (Author, Document Field, etc.)
    if (isCommonAnno) {
      const vals = filter.values;
      const hasValues = Array.isArray(vals) ? vals.length > 0 : vals != null;
      if (!hasValues) continue;

      const config = commonAnnoFilterMap.get(filter.filterId);
      const { icon: levelIcon } = config ? getFilterIcon(config) : { icon: "Funnel" };

      blocks.push({
        key: filter.filterId,
        filterId: filter.filterId,
        level: "commonAnnoFilter",
        levelIcon,
        levelLabel: config?.display || filter.filterId,
        breadcrumb: [],
        parentThemeId: null,
        parentSubThemeId: null,
        values: filter.values,
        filterConfig: config,
      });
      continue;
    }

    // Hierarchy filters
    const meta = LEVEL_META[filter.filterId];
    if (!meta || !filter.values?.length) continue;

    if (meta.level === "themes") {
      // Themes have no parent grouping — one block for all themes
      blocks.push({
        key: filter.filterId,
        filterId: filter.filterId,
        ...meta,
        breadcrumb: [],
        parentThemeId: null,
        parentSubThemeId: null,
        values: filter.values,
      });
    } else if (meta.level === "subThemes") {
      // Group by annoFilterBucketKey (parentThemeId, with independent → "__independent__")
      const grouped = new Map();
      for (const v of filter.values) {
        const annoFilterBucketKey = getAnnoFilterBucketKey(meta.level, v);
        if (!grouped.has(annoFilterBucketKey)) {
          grouped.set(annoFilterBucketKey, {
            parentThemeId: v.parentThemeId || null,
            parentThemeDisplay: v.parentThemeDisplay || null,
            values: [],
          });
        }
        grouped.get(annoFilterBucketKey).values.push(v);
      }
      for (const [annoFilterBucketKey, group] of grouped) {
        blocks.push({
          key: `${filter.filterId}::${annoFilterBucketKey}`,
          filterId: filter.filterId,
          annoFilterBucketKey,
          ...meta,
          breadcrumb: group.parentThemeDisplay
            ? [group.parentThemeDisplay]
            : [],
          parentThemeId: group.parentThemeId,
          parentSubThemeId: null,
          values: group.values,
        });
      }
    } else if (meta.level === "tags") {
      // Group by annoFilterBucketKey (parentThemeId::parentSubThemeId)
      const grouped = new Map();
      for (const v of filter.values) {
        const annoFilterBucketKey = getAnnoFilterBucketKey(meta.level, v);
        if (!grouped.has(annoFilterBucketKey)) {
          grouped.set(annoFilterBucketKey, {
            parentThemeId: v.parentThemeId || null,
            parentThemeDisplay: v.parentThemeDisplay || null,
            parentSubThemeId: v.parentSubThemeId || null,
            parentSubThemeDisplay: v.parentSubThemeDisplay || null,
            values: [],
          });
        }
        grouped.get(annoFilterBucketKey).values.push(v);
      }
      for (const [annoFilterBucketKey, group] of grouped) {
        const breadcrumb = [];
        if (group.parentThemeDisplay) breadcrumb.push(group.parentThemeDisplay);
        if (group.parentSubThemeDisplay)
          breadcrumb.push(group.parentSubThemeDisplay);

        blocks.push({
          key: `${filter.filterId}::${annoFilterBucketKey}`,
          filterId: filter.filterId,
          annoFilterBucketKey,
          ...meta,
          breadcrumb,
          parentThemeId: group.parentThemeId,
          parentSubThemeId: group.parentSubThemeId,
          values: group.values,
        });
      }
    }
  }

  // --- Empty blocks for saved configs without active values ---
  const coveredKeys = new Set(blocks.map((b) => b.key));

  for (const config of savedConfigs) {
    const isHierarchy = ANNO_HIERARCHY_FILTER_IDS.has(config.filterId);
    const isCommonAnno = commonAnnoFilterIds.has(config.filterId);
    if (!isHierarchy && !isCommonAnno) continue;

    if (isCommonAnno) {
      if (coveredKeys.has(config.filterId)) continue;
      const commonConfig = commonAnnoFilterMap.get(config.filterId);
      const { icon: levelIcon } = commonConfig
        ? getFilterIcon(commonConfig)
        : { icon: "Funnel" };
      blocks.push({
        key: config.filterId,
        filterId: config.filterId,
        level: "commonAnnoFilter",
        levelIcon,
        levelLabel: commonConfig?.display || config.display || config.filterId,
        breadcrumb: [],
        parentThemeId: null,
        parentSubThemeId: null,
        values: [],
        filterConfig: commonConfig || config,
      });
      continue;
    }

    // Hierarchy filter — use _savedGroups if available to preserve breadcrumbs
    const meta = LEVEL_META[config.filterId];
    if (!meta) continue;

    if (meta.level === "themes") {
      if (coveredKeys.has(config.filterId)) continue;
      blocks.push({
        key: config.filterId,
        filterId: config.filterId,
        ...meta,
        breadcrumb: [],
        parentThemeId: null,
        parentSubThemeId: null,
        values: [],
      });
    } else if (meta.level === "subThemes") {
      const savedGroups = config._savedGroups || [{ parentThemeId: null }];
      for (const group of savedGroups) {
        const annoFilterBucketKey = getAnnoFilterBucketKey(meta.level, group);
        const blockKey = `${config.filterId}::${annoFilterBucketKey}`;
        if (coveredKeys.has(blockKey)) continue;
        blocks.push({
          key: blockKey,
          filterId: config.filterId,
          annoFilterBucketKey,
          ...meta,
          breadcrumb: group.parentThemeDisplay
            ? [group.parentThemeDisplay]
            : [],
          parentThemeId: group.parentThemeId || null,
          parentSubThemeId: null,
          values: [],
        });
      }
    } else if (meta.level === "tags") {
      const savedGroups = config._savedGroups || [
        { parentThemeId: null, parentSubThemeId: null },
      ];
      for (const group of savedGroups) {
        const annoFilterBucketKey = getAnnoFilterBucketKey(meta.level, group);
        const blockKey = `${config.filterId}::${annoFilterBucketKey}`;
        if (coveredKeys.has(blockKey)) continue;
        const breadcrumb = [];
        if (group.parentThemeDisplay) breadcrumb.push(group.parentThemeDisplay);
        if (group.parentSubThemeDisplay)
          breadcrumb.push(group.parentSubThemeDisplay);
        blocks.push({
          key: blockKey,
          filterId: config.filterId,
          annoFilterBucketKey,
          ...meta,
          breadcrumb,
          parentThemeId: group.parentThemeId || null,
          parentSubThemeId: group.parentSubThemeId || null,
          values: [],
        });
      }
    }
  }

  return blocks;
};
